import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../utils/api';

// Create Auth Context
const AuthContext = createContext(null);

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const savedAdmin = localStorage.getItem('admin_user');

      console.log('🔍 Checking auth...', {
        hasToken: !!token,
        hasAdmin: !!savedAdmin,
        tokenPreview: token ? token.substring(0, 20) + '...' : 'none'
      });

      if (token && savedAdmin) {
        // Verify token is still valid
        try {
          console.log('📡 Calling /auth/me endpoint...');
          const response = await authAPI.getMe();
          console.log('✅ Auth check response:', response.data);

          if (response.data.success) {
            setAdmin(response.data.data);
            setIsAuthenticated(true);
            console.log('✅ Auth restored successfully for:', response.data.data.email);
          } else {
            // Token invalid, clear storage
            console.log('❌ Auth check failed: invalid response');
            await clearAuth();
          }
        } catch (error) {
          console.log('❌ Auth check error:', {
            status: error.response?.status,
            code: error.response?.data?.error?.code,
            message: error.response?.data?.error?.message,
            isNetworkError: !error.response
          });

          // If it's a 401, the token is invalid or expired
          if (error.response?.status === 401) {
            const errorCode = error.response?.data?.error?.code;
            console.log(`❌ Auth check failed with ${errorCode}: Clearing auth and redirecting to login`);
            await clearAuth();
          } else if (!error.response) {
            // Network error - keep the user logged in locally
            console.log('⚠️  Network error during auth check, keeping local session');
            try {
              const parsedAdmin = JSON.parse(savedAdmin);
              setAdmin(parsedAdmin);
              setIsAuthenticated(true);
            } catch (parseError) {
              console.error('❌ Failed to parse saved admin:', parseError);
              await clearAuth();
            }
          } else {
            // Other server errors - keep local session
            console.log('⚠️  Server error during auth check, keeping local session');
            try {
              const parsedAdmin = JSON.parse(savedAdmin);
              setAdmin(parsedAdmin);
              setIsAuthenticated(true);
            } catch (parseError) {
              console.error('❌ Failed to parse saved admin:', parseError);
              await clearAuth();
            }
          }
        }
      } else {
        console.log('ℹ️  No token or admin data found in localStorage');
      }
    } catch (error) {
      console.error('❌ Unexpected error in checkAuth:', error);
    } finally {
      setLoading(false);
      console.log('✅ Auth check complete. Loading state set to false.');
    }
  };

  const clearAuth = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setAdmin(null);
    setIsAuthenticated(false);
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);

      if (response.data.success) {
        const { token, admin: adminData } = response.data.data;

        // Store token and admin info
        localStorage.setItem('admin_token', token);
        localStorage.setItem('admin_user', JSON.stringify(adminData));

        setAdmin(adminData);
        setIsAuthenticated(true);

        return { success: true, admin: adminData };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Login failed'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          'Login failed. Please check your credentials.';
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state regardless of API call result
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      setAdmin(null);
      setIsAuthenticated(false);
    }
  };

  const refreshAuth = async () => {
    try {
      const response = await authAPI.getMe();
      if (response.data.success) {
        const adminData = response.data.data;
        setAdmin(adminData);
        localStorage.setItem('admin_user', JSON.stringify(adminData));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Refresh auth failed:', error);
      return false;
    }
  };

  const hasPermission = (permission) => {
    if (!admin) return false;

    // Super admin has all permissions
    if (admin.permissions?.includes('*')) return true;

    // Check specific permission
    return admin.permissions?.includes(permission) || false;
  };

  const hasRole = (role) => {
    if (!admin) return false;
    return admin.role === role;
  };

  const value = {
    admin,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshAuth,
    hasPermission,
    hasRole
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
